// Magnus PFMEA Types
// ============================================================
// Comprehensive TypeScript types for PFMEA module
// Supports multi-standard, 4M analysis, and Control Plan integration
// ============================================================

// Re-export ProcessElement4M from processFlow.ts for backwards compatibility
export { ProcessElement4M } from './types/processFlow';

// ============================================================
// PFMEA STANDARDS - Top Industry Coverage
// ============================================================

export type PfmeaStandard =
    | 'AIAG_VDA'      // Automotive (AIAG-VDA 2019)
    | 'AIAG_V4'       // Automotive (AIAG 4th Edition)
    | 'ISO_14971'     // Medical Devices
    | 'AS9100D'       // Aerospace
    | 'MIL_STD_1629A' // Military/Defense
    | 'IEC_61508'     // Functional Safety
    | 'ISO_9001'      // Generic QMS
    | 'IATF_16949';   // Automotive Supply Chain

export type ProcessDocStandard =
    | 'AIAG_CONTROL_PLAN'
    | 'VDA_CONTROL_PLAN'
    | 'AIAG_PROCESS_FLOW'
    | 'WORK_INSTRUCTION_ISO'
    | 'CUSTOM';

export type IndustryCompliance =
    | 'IATF_16949'
    | 'AS9100D'
    | 'FDA_21_CFR_820'
    | 'ISO_13485'
    | 'ISO_9001'
    | 'IEC_61508'
    | 'ISO_26262'
    | 'MIL_STD_1629A'
    | 'CMMC_LEVEL_2';

// ============================================================
// 4M CAUSE CATEGORIES
// ============================================================

export type CauseCategory =
    | 'Man'
    | 'Machine'
    | 'Material'
    | 'Method'
    | 'Measurement'
    | 'Environment'
    // Medical-specific
    | 'User Error'
    | 'Device Failure'
    | 'Environmental'
    | 'Manufacturing'
    // Aerospace/Military
    | 'Design'
    | 'Maintenance'
    | 'Operations'
    | 'Human Factors'
    // Functional Safety
    | 'Random HW Fault'
    | 'Systematic Fault'
    | 'CCF'
    | 'External Interference'
    | 'Software'
    // Generic
    | 'People'
    | 'Process'
    | 'Technology'
    | 'External';

// ============================================================
// PROCESS FLOW DIAGRAM & STRUCTURE TREE (AIAG-VDA Step 2)
// ============================================================

export type ProcessStepType =
    | 'operation'
    | 'inspection'
    | 'transport'
    | 'storage'
    | 'decision'
    | 'rework';

// AIAG-VDA Structure Tree: Work Element (lowest level)
export interface WorkElement {
    id: string;
    name: string;                 // e.g., "Electrode positioning"
    description: string;
    processStepId: string;        // Parent Process Step
    duration?: number;            // seconds
    tooling?: string;
    failureRisks?: string[];      // Associated failure modes
}

// AIAG-VDA Structure Tree: Process Item (highest level)
export interface ProcessItem {
    id: string;
    name: string;                 // e.g., "Welding Assembly"
    description: string;
    processStepIds: string[];     // Child Process Steps
    projectName: string;
    order: number;                // Sequence in overall process
}

// AIAG-VDA Structure Tree: Process Step (middle level)
export interface ProcessStep {
    id: string;
    stepNumber: string;           // e.g., "OP-020"
    stepName: string;
    stepType: ProcessStepType;
    description: string;
    equipment?: string;
    operator?: string;
    cycleTime?: number;           // seconds
    specialCharacteristics?: string[];
    position?: { x: number; y: number };
    linkedPfmeaIds?: string[];    // Made optional for flexibility

    // Control Plan / Flow integration fields
    characteristic?: string;
    specification?: string;
    controlMethod?: string;
    sampleSize?: string;
    sampleFrequency?: string;

    // Structure Tree Hierarchy (NEW)
    parentProcessItemId?: string; // Link to parent Process Item
    workElementIds?: string[];    // Made optional - links to child Work Elements
    processElement?: CauseCategory; // 4M category
}

export interface ProcessFlowDiagram {
    id: string;
    projectName: string;
    projectId?: string;
    epochId?: number;
    title: string;
    name?: string;               // Flexibility for different components
    description?: string;
    revision?: string;
    steps?: ProcessStep[];       // Legacy/Table view support

    // React Flow support
    nodes?: any[];
    edges?: any[];
    aiagVdaStep?: number;

    status: 'draft' | 'review' | 'approved';
    version?: string;
    createdAt?: string;
    updatedAt?: string;
    createdBy?: string;
    approvedBy?: string;
    approvedAt?: string;
}

// ============================================================
// FUNCTION ANALYSIS (AIAG-VDA Step 3)
// ============================================================

// Product Requirements (Customer/Engineering specifications)
export interface ProductRequirement {
    id: string;
    projectName: string;
    requirementNumber: string;     // e.g., "REQ-001"
    requirementName: string;
    description: string;
    type: 'customer' | 'regulatory' | 'engineering' | 'safety';
    specification?: string;        // e.g., "Tensile strength ≥ 500 MPa"
    tolerance?: string;            // e.g., "±0.05mm"
    linkedProductCharacteristics: string[]; // IDs of characteristics that ensure this requirement
}

// Characteristics (Measurable attributes of the product)
export interface ProductCharacteristic {
    id: string;
    name: string;
    description: string;
    measurementUnit?: string;      // e.g., "mm", "MPa", "°C"
    nominalValue?: number;
    toleranceLower?: number;
    toleranceUpper?: number;
    specialCharacteristic?: 'CC' | 'SC' | 'none';
}

// Process Functions (How the process ensures product characteristics)
export interface ProcessFunction {
    id: string;
    processStepId: string;         // Link to Process Step
    functionDescription: string;   // e.g., "Ensure hole diameter within spec"
    productCharacteristicId?: string; // What characteristic this function ensures
    productRequirementId?: string; // What requirement this supports
    parameters?: string[];         // Process parameters (temp, pressure, time)
    // Function Net connections
    inputFunctions?: string[];     // Functions that feed into this one
    outputFunctions?: string[];    // Functions that this feeds into
}

// ============================================================
// PFMEA ITEM (Core Data Structure)
// ============================================================

export interface ApprovalSignature {
    action: 'submitted' | 'approved' | 'rejected';
    user: string;
    timestamp: string;
    comment?: string;
    role?: string;
}

export interface RecommendedAction {
    id: string;
    description: string;
    responsiblePerson: string;
    targetDate: string;
    status: 'open' | 'in-progress' | 'completed' | 'cancelled';
    completedDate?: string;
    revisedSeverity?: number;
    revisedOccurrence?: number;
    revisedDetection?: number;
    notes?: string;
}

export interface ActionWithContext extends RecommendedAction {
    pfmeaItem: PfmeaItem;
    originalRPN: number;
    revisedRPN?: number;
}

export interface PfmeaItem {
    id: string;
    projectName: string;
    epochId?: number;
    standard: PfmeaStandard;

    // Process Identification
    processStepNumber: string;    // e.g., "OP-020"
    processStepName: string;
    processElement: CauseCategory;
    processFunction: string;
    requirement?: string;

    // Failure Analysis
    failureMode: string;
    failureEffectLocal?: string;
    failureEffectNext?: string;
    failureEffectEnd: string;
    failureCause: string;

    // Risk Analysis (1-10 scales for most standards)
    severity: number;
    occurrence: number;
    detection: number;

    // Calculated Fields
    actionPriority?: 'H' | 'M' | 'L';
    rpn?: number;

    // Controls
    preventionControls: string[];
    detectionControls: string[];

    // Special Characteristics
    specialCharacteristic?: 'CC' | 'SC' | 'none';

    // Traceability
    linkedControlPlanId?: string;
    linkedWorkInstructionId?: string;
    linkedProcessStepId?: string;
    linkedBomItemIds?: number[];
    linkedFunctionId?: string;     // NEW - Link to ProcessFunction
    linkedDfmeaId?: string;        // NEW - Link to Design FMEA item for Traceability

    // Actions
    recommendedActions: RecommendedAction[];

    // Metadata
    status: 'draft' | 'review' | 'approved';
    createdBy: string;
    createdAt: string | Date;
    updatedAt: string | Date;
    approvedBy?: string;
    approvedAt?: string | Date;
    modifiedBy?: string; // For legacy support
    changeHistory?: Array<{
        timestamp: string;
        user: string;
        field: string;
        oldValue: string;
        newValue: string;
        comment?: string;
    }>;

    // Legacy Support
    failureEffect?: string;

    // Medical/Aerospace specific
    hazard?: string;
    harm?: string;
    riskLevel?: string;
    residualRisk?: string;

    // Functional Safety specific
    silLevel?: string;
    asilLevel?: string;
    safetyGoalViolation?: string;
    diagnosticCoverage?: number;
}

// ============================================================
// CONTROL PLAN
// ============================================================

export interface ControlPlanItem {
    id: string;
    processStepNumber: string;
    processStepName: string;
    machine?: string;
    characteristic: string;
    characteristicType: 'product' | 'process';
    specialCharacteristic?: 'CC' | 'SC' | 'none';
    specification: string;
    evaluationTechnique: string;
    sampleSize: string;
    sampleFrequency: string;
    controlMethod: string;
    reactionPlan: string;
    linkedPfmeaId?: string;
}

export interface WorkInstruction {
    id: string;
    processStepId: string;
    stepNumber: string;
    stepName: string;
    description: string;
    hazards: string[];
    ppe: string[];
    steps: Array<{
        order: number;
        instruction: string;
        image?: string;
        videoUrl?: string;
    }>;
    lastUpdated: string;
    projectName: string;
}

export interface ControlPlan {
    id: string;
    projectName: string;
    epochId?: number;
    partNumber: string;
    partName: string;
    revision: string;
    phase: 'prototype' | 'pre-launch' | 'production';
    standard: ProcessDocStandard;
    preparedBy: string;
    approvedBy?: string;
    items: ControlPlanItem[];
}

// ============================================================
// USER & AUTH
// ============================================================

export type UserRole =
    | 'viewer'
    | 'engineer'
    | 'org_admin'
    | 'platform_admin'
    | 'pending'        // ISO 27001: Users awaiting approval
    | 'autoGainAdmin'; // Platform super admin

export interface User {
    uid: string;
    email: string | null;
    displayName: string | null;
    photoURL: string | null;
    role: UserRole;
    organizationId?: string;
    permissions: string[];
    allowedApps?: string[];
    lastLogin?: Date;
    isApproved?: boolean;  // ISO 27001: Explicit approval flag
}

// ============================================================
// DASHBOARD METRICS
// ============================================================

export interface DashboardMetrics {
    totalPfmeaItems: number;
    byActionPriority: {
        high: number;
        medium: number;
        low: number;
    };
    by4M: {
        Man: number;
        Machine: number;
        Material: number;
        Method: number;
        [key: string]: number;
    };
    bySpecialCharacteristic: {
        CC: number;
        SC: number;
        none: number;
    };
    openActions: number;
    completedActions: number;
    overdueActions: number;
    processSteps: number;
    controlPlanItems: number;
    averageSeverity: number;
    highRiskItems: PfmeaItem[];
    recentChanges: {
        id: string;
        type: string;
        description: string;
        timestamp: Date;
        user: string;
    }[];

    // Data Quality & Completeness Metrics
    dataQuality: {
        // Item counts
        completePfmeaItems: number;      // Items with all required fields
        incompletePfmeaItems: number;    // Items missing required fields

        // Link tracking
        itemsWithDfmeaLinks: number;     // Items linked to DFMEA/BOM
        itemsWithControlPlanLinks: number; // Items linked to Control Plan
        itemsWithProcessStepLinks: number; // Items linked to Process Steps

        // Missing data breakdown
        missingFailureEffects: number;   // Items missing effect chain
        missingControls: number;         // Items with no prevention/detection controls
        missingRatings: number;          // Items missing S/O/D ratings
        missingActions: number;          // High-risk items with no actions

        // Auditor Red Flags (Strict)
        safetyViolations?: number;       // S>=9 items with no actions (MAJOR)
        orphanCriticals?: number;        // CC items missing from Control Plan (MAJOR)
        orphanedProcessSteps?: number;   // Process steps missing from PFMEA (MAJOR)

        // Special characteristics
        unclassifiedCharacteristics: number; // Items with no CC/SC classification

        // Completeness percentage
        overallCompleteness: number;     // 0-100% score

        // Lists for drill-down
        incompleteItemIds: string[];     // For linking to PFMEA page
    };
}

// ============================================================
// AIAG-VDA 7-STEP WORKFLOW
// ============================================================

export interface WorkflowStep {
    id: number;
    name: string;
    description: string;
    status: 'not-started' | 'in-progress' | 'completed' | 'failed';  // Added 'failed' for non-conformance
    completedPercentage: number;
    linkedPages: string[];
}

export const AIAG_VDA_WORKFLOW: WorkflowStep[] = [
    { id: 1, name: 'Planning & Preparation', description: 'Define scope, team, timing, and resources', status: 'not-started', completedPercentage: 0, linkedPages: ['/projects', '/project-scoping', '/linked-data'] },
    { id: 2, name: 'Structure Analysis', description: 'Define process elements and hierarchy', status: 'not-started', completedPercentage: 0, linkedPages: ['/process-flow'] },
    { id: 3, name: 'Function Analysis', description: 'Define functions of each process step', status: 'not-started', completedPercentage: 0, linkedPages: ['/function-matrix', '/process-flow'] },
    { id: 4, name: 'Failure Analysis', description: 'Identify failure modes, effects, causes', status: 'not-started', completedPercentage: 0, linkedPages: ['/pfmea-editor', '/ideation', '/failure-net'] },
    { id: 5, name: 'Risk Analysis', description: 'Rate severity, occurrence, detection', status: 'not-started', completedPercentage: 0, linkedPages: ['/pfmea'] },
    { id: 6, name: 'Optimization', description: 'Define actions to reduce risk', status: 'not-started', completedPercentage: 0, linkedPages: ['/actions', '/workflow'] },
    { id: 7, name: 'Documentation', description: 'Document results and approvals', status: 'not-started', completedPercentage: 0, linkedPages: ['/control-plan', '/work-instructions', '/reports'] },
];

// ============================================================
// PAGE CONTEXT FOR AI ASSISTANT
// ============================================================

export interface PageContext {
    id: string;
    name: string;
    purpose: string;
    aiagVdaStep: number[];
    relatedAgents: string[];
    nextSteps: string[];
}

export const PAGE_CONTEXTS: Record<string, PageContext> = {
    '/': {
        id: 'dashboard',
        name: 'PFMEA Dashboard',
        purpose: 'View PFMEA metrics, risk distribution, and workflow progress',
        aiagVdaStep: [1],
        relatedAgents: ['risk-assessment', 'report-generation'],
        nextSteps: ['Review high-risk items', 'Check overdue actions', 'Navigate to specific pages'],
    },
    '/process-flow': {
        id: 'process-flow',
        name: 'Process Flow Diagram',
        purpose: 'Define and visualize manufacturing process steps',
        aiagVdaStep: [2, 3],
        relatedAgents: ['process-flow', 'structure-function'],
        nextSteps: ['Add process steps', 'Link to PFMEA items', 'Generate with AI'],
    },
    '/pfmea': {
        id: 'pfmea-table',
        name: 'PFMEA Worksheet',
        purpose: 'Analyze process failure modes, effects, and causes with S/O/D ratings',
        aiagVdaStep: [4, 5],
        relatedAgents: ['failure-analysis', 'risk-assessment', '4m-analysis', 'pfmea-ideation'],
        nextSteps: ['Add failure modes', 'Rate S/O/D', 'Define controls', 'Create actions'],
    },
    '/ideation': {
        id: 'ai-ideation',
        name: 'AI PFMEA Ideation',
        purpose: 'Generate PFMEA content using AI with 4M analysis',
        aiagVdaStep: [2, 3, 4],
        relatedAgents: ['pfmea-ideation', 'process-flow', '4m-analysis'],
        nextSteps: ['Describe process', 'Generate steps', 'Generate failures', 'Review and save'],
    },
    '/control-plan': {
        id: 'control-plan',
        name: 'Control Plan',
        purpose: 'Document process controls, inspection methods, and reaction plans',
        aiagVdaStep: [6, 7],
        relatedAgents: ['control-plan', 'compliance-validation'],
        nextSteps: ['Generate from PFMEA', 'Define sample sizes', 'Add reaction plans'],
    },
    '/work-instructions': {
        id: 'work-instructions',
        name: 'Work Instructions',
        purpose: 'Create operator instructions linked to process steps',
        aiagVdaStep: [7],
        relatedAgents: ['report-generation'],
        nextSteps: ['Create instructions', 'Add images/videos', 'Link to process steps'],
    },
    '/settings': {
        id: 'settings',
        name: 'Settings',
        purpose: 'Configure PFMEA standard, project settings, and preferences',
        aiagVdaStep: [1],
        relatedAgents: [],
        nextSteps: ['Select standard', 'Configure project', 'Set permissions'],
    },
};

// ============================================================
// PROJECT CONFIGURATION & SCOPING (AIAG-VDA Step 1)
// ============================================================

// 5T Framework for AIAG-VDA Planning & Preparation
export interface ProjectScoping {
    // InTent - Project goals and scope
    intent: {
        goals: string[];
        scopeBoundaries: string;
        assumptions: string[];
        exclusions?: string[];
    };

    // Timing - Schedule and milestones
    timing: {
        kickoffDate: Date;
        targetCompletionDate: Date;
        milestones: Array<{
            name: string;
            date: Date;
            status: 'pending' | 'in-progress' | 'completed';
        }>;
    };

    // Team - Cross-functional team members
    team: {
        lead: string;
        members: Array<{
            name: string;
            role: string;
            department: string;
            expertise: string;
        }>;
    };

    // Tools - Methods and resources
    tools: {
        software: string[];
        templates: string[];
        standards: string[];
        references: string[];
    };

    // Tasks - Work breakdown
    tasks: Array<{
        id: string;
        name: string;
        owner: string;
        dueDate: Date;
        status: 'not-started' | 'in-progress' | 'completed' | 'blocked';
        dependencies?: string[];
    }>;
}

export interface ProjectConfig {
    id: string;
    name: string;
    description?: string;
    standard: PfmeaStandard;
    compliance: IndustryCompliance[];
    partNumber?: string;
    partName?: string;
    customer?: string;
    team?: string[];
    createdAt: Date;
    updatedAt: Date;

    // 5T Scoping (NEW)
    scoping?: ProjectScoping;
}

// ============================================================
// PROCESS LINE (Multi-Line River Flow) - NEW
// ============================================================

export type ProcessLineType = 'main' | 'feeder' | 'sub_process' | 'rework';

export interface ProcessLine {
    id: string;
    projectId: string;
    name: string;
    lineType: ProcessLineType;
    sequence: number;
    description?: string;
    outputsTo?: {
        targetLineId: string;
        targetStepId: string;
        connectionType: 'material' | 'assembly' | 'data' | 'inspection';
    };
    inputsFrom?: Array<{
        sourceLineId: string;
        sourceStepId: string;
        connectionType: 'material' | 'assembly' | 'data' | 'inspection';
    }>;
    steps: ProcessStep[];
    position?: { x: number; y: number };
    color?: string;
}

// ============================================================
// MASTER FMEA INHERITANCE - NEW
// ============================================================

export interface InheritedItem {
    sourceItemId: string;
    isInherited: boolean;
    overrideFields: string[];
    lockedByMaster: boolean;
    lastSyncedAt?: string;
    syncStatus: 'synced' | 'modified' | 'conflict' | 'detached';
}

export interface PfmeaItemInheritance {
    inheritedFrom?: InheritedItem;
}

export interface MasterSyncResult {
    itemsAdded: number;
    itemsUpdated: number;
    conflicts: Array<{
        itemId: string;
        field: string;
        masterValue: unknown;
        localValue: unknown;
    }>;
    syncedAt: string;
}

export interface ConnectionPoint {
    id: string;
    lineId: string;
    stepId: string;
    pointType: 'input' | 'output';
    position: { x: number; y: number };
    connectedTo?: {
        lineId: string;
        stepId: string;
        connectionPointId: string;
    };
    materialFlow?: string;
}
